import * as XLSX from "xlsx";
import { ExpenseReport } from "../models/expense_report";

export const downloadExcel = (
  reports: ExpenseReport[],
  exportName: string,
  t?: any,
) => {
  const translateCat = t?.categoryTypes || {};
  const tStatus = t?.expenseReports || {};
  const locale = t?.common?.locale || "fr-FR";

  const getStatusLabel = (status: string) => {
    if (!status) return "";
    const map: Record<string, string> = {
      DRAFT: tStatus.statusDraft,
      PENDING: tStatus.statusPending,
      APPROVED: tStatus.statusApproved,
      REJECTED: tStatus.statusRejected,
      REIMBURSED: tStatus.statusReimbursed,
    };
    return map[status.toUpperCase()] || status;
  };

  const detailsHeaders = [
    t?.expenseReports?.tableDate || "Date",
    t?.expenseReportDetails?.fields?.user || "Collaborateur",
    t?.expenseReports?.tableCategory || "Catégorie",
    t?.expenseReports?.tableSupplier || "Fournisseur",
    t?.expenseReportDetails?.fields?.comment || "Description",
    t?.expenseReportDetails?.itemFields?.amountHt || "Montant HT",
    t?.expenseReports?.tableVat || "TVA",
    t?.expenseReportDetails?.itemFields?.totalAmountTtc || "Montant TTC",
    t?.expenseReports?.tableStatus || "Statut",
  ];

  let totalTTC = 0,
    totalHT = 0,
    totalVAT = 0;
  const sortedReports = [...reports].sort(
    (a, b) =>
      new Date(a.expense_date || 0).getTime() -
      new Date(b.expense_date || 0).getTime(),
  );

  const detailsData = sortedReports.map((r) => {
    const date = r.expense_date
      ? new Date(r.expense_date).toLocaleDateString(locale)
      : "";
    const actualEmployee =
      `${r.user?.first_name || ""} ${r.user?.last_name || ""}`.trim() ||
      exportName;
    const rawCat = r.category?.item_type || "other";
    const displayCat = translateCat[rawCat.toLowerCase()] || rawCat;
    const supplier = r.supplier_name || "";
    const description = (r.comment || "").replace(/\n/g, " ");

    const amountTTC = Number(r.amount) || 0;
    const vatAmount = Number(r.vat_amount) || 0;
    const amountHT = amountTTC - vatAmount;

    totalTTC += amountTTC;
    totalVAT += vatAmount;
    totalHT += amountHT;

    return [
      date,
      actualEmployee,
      displayCat,
      supplier,
      description,
      amountHT,
      vatAmount,
      amountTTC,
      getStatusLabel(r.status || ""),
    ];
  });

  detailsData.push([
    "",
    "",
    "",
    "",
    t?.expenseReports?.totals || "TOTAUX",
    totalHT,
    totalVAT,
    totalTTC,
    "",
  ]);

  const summaryHeaders = [
    t?.expenseReports?.tableCategory || "Famille / Catégorie",
    t?.expenseReportDetails?.itemFields?.amountHt || "Total HT",
    t?.expenseReports?.tableVat || "Total TVA",
    t?.expenseReportDetails?.itemFields?.totalAmountTtc || "Total TTC",
  ];

  const groupedReports: Record<
    string,
    { ht: number; tva: number; ttc: number }
  > = {};

  reports.forEach((r) => {
    const rawCat = r.category?.item_type || "other";
    const displayCat = translateCat[rawCat.toLowerCase()] || rawCat;

    if (!groupedReports[displayCat]) {
      groupedReports[displayCat] = { ht: 0, tva: 0, ttc: 0 };
    }

    const amountTTC = Number(r.amount) || 0;
    const vatAmount = Number(r.vat_amount) || 0;

    groupedReports[displayCat].ttc += amountTTC;
    groupedReports[displayCat].tva += vatAmount;
    groupedReports[displayCat].ht += amountTTC - vatAmount;
  });

  const summaryData = Object.entries(groupedReports).map(([cat, totals]) => {
    return [cat, totals.ht, totals.tva, totals.ttc];
  });

  const wb = XLSX.utils.book_new();

  const wsDetails = XLSX.utils.aoa_to_sheet([detailsHeaders, ...detailsData]);
  const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryData]);

  XLSX.utils.book_append_sheet(wb, wsDetails, t.common?.details || "Détails");
  XLSX.utils.book_append_sheet(
    wb,
    wsSummary,
    t.common?.accountingSummary || "Synthèse comptable",
  );

  const filename = `export_${exportName.replace(/\s+/g, "_")}_${new Date().getTime()}.xlsx`;
  XLSX.writeFile(wb, filename);
};
